import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, FileSignature, Plus, Trash2 } from 'lucide-react';
import { ErreurApi } from '../../lib/api';
import {
  usePositionnementsCandidat,
  usePositionner,
  useSupprimerImmersion,
  useTrancherImmersion,
  type LigneImmersion,
} from '../../lib/useMatching';
import { libellePositionnement, periodePositionnement } from '../../lib/immersion-statut';
import { Chargement, MessageErreur, Pastille } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { FormulairePositionnement } from './FormulairePositionnement';
import { ModificationFinImmersion } from './ModificationFinImmersion';

type Geste = 'OUI' | 'NON' | 'SUPPRIMER';

const CONFIRMATIONS: Record<Geste, { question: string; action: string }> = {
  OUI: {
    question:
      'L’entreprise dit oui : le pôle administratif sera prévenu pour concevoir les documents contractuels.',
    action: 'Confirmer le oui',
  },
  NON: {
    question: 'L’entreprise dit non : cette immersion sera close.',
    action: 'Confirmer le non',
  },
  SUPPRIMER: {
    question: 'Supprimer cette immersion saisie par erreur ?',
    action: 'Supprimer',
  },
};

const CLASSE_BOUTON =
  'inline-flex min-h-[38px] items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-[11px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 [@media(pointer:coarse)]:min-h-[40px]';

function jourCourt(valeur: string): string {
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

function messageErreur(erreur: unknown): string | null {
  return erreur instanceof ErreurApi ? erreur.message : null;
}

function LignePositionnement({
  immersion,
  peutEtablirContrat,
}: {
  immersion: LigneImmersion;
  peutEtablirContrat: boolean;
}): JSX.Element {
  const notifier = useToast();
  const trancher = useTrancherImmersion();
  const supprimer = useSupprimerImmersion();
  const [geste, setGeste] = useState<Geste | null>(null);

  const pastille = libellePositionnement(immersion);
  const aTrancher = immersion.refusNon === null;
  const enCours = trancher.isPending || supprimer.isPending;
  const erreur = messageErreur(trancher.error) ?? messageErreur(supprimer.error);

  const confirmer = (): void => {
    const terminer = {
      onSuccess: ({ message }: { message: string }) => {
        notifier(message);
        setGeste(null);
      },
    };
    if (geste === 'SUPPRIMER') supprimer.mutate(immersion.id, terminer);
    if (geste === 'OUI') trancher.mutate({ id: immersion.id, statut: 'VALIDEE' }, terminer);
    if (geste === 'NON') trancher.mutate({ id: immersion.id, statut: 'NON_VALIDEE' }, terminer);
  };

  return (
    <li className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-codialis-sand text-codialis-navy">
            <Building2 className="h-4 w-4" aria-hidden />
          </span>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-codialis-navy">
              {immersion.entreprise}
            </div>
            <div className="text-[11px] text-codialis-gray">
              {periodePositionnement(immersion, jourCourt)}
            </div>
          </div>
        </div>
        <Pastille texte={pastille.texte} ton={pastille.ton} />
      </div>

      {immersion.statut === 'VALIDEE' && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-[11px] text-emerald-900">
          <span>Pôle administratif prévenu pour les documents contractuels.</span>
          {peutEtablirContrat && (
            <Link
              to={`/adm-contrats-apprentissage/${immersion.id}`}
              target="_blank"
              rel="noreferrer"
              className={`${CLASSE_BOUTON} border border-emerald-700/30 text-emerald-900 hover:bg-white`}
            >
              <FileSignature className="h-3.5 w-3.5" aria-hidden />
              Établir le contrat
            </Link>
          )}
        </div>
      )}

      {aTrancher && geste === null && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
              Validée ?
            </span>
            <button
              type="button"
              onClick={() => setGeste('OUI')}
              disabled={immersion.refusOui !== null}
              className={`${CLASSE_BOUTON} bg-emerald-700 text-white hover:bg-emerald-800`}
            >
              Oui
            </button>
            <button
              type="button"
              onClick={() => setGeste('NON')}
              className={`${CLASSE_BOUTON} border border-rose-200 text-rose-700 hover:bg-rose-50`}
            >
              Non
            </button>
            {immersion.refusSuppression === null && (
              <button
                type="button"
                onClick={() => setGeste('SUPPRIMER')}
                className={`${CLASSE_BOUTON} ml-auto text-codialis-gray hover:bg-gray-50`}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
                Supprimer
              </button>
            )}
          </div>
          {immersion.refusOui && (
            <p className="text-[11px] text-codialis-gray">{immersion.refusOui}</p>
          )}
          <ModificationFinImmersion key={immersion.dateFin ?? ''} immersion={immersion} />
        </div>
      )}

      {geste && (
        <div className="space-y-2 rounded-lg bg-amber-50 p-3">
          <p className="text-[11px] text-amber-900">{CONFIRMATIONS[geste].question}</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={confirmer}
              disabled={enCours}
              className={`${CLASSE_BOUTON} bg-codialis-navy text-white hover:bg-codialis-deep`}
            >
              {enCours ? 'Enregistrement…' : CONFIRMATIONS[geste].action}
            </button>
            <button
              type="button"
              onClick={() => setGeste(null)}
              disabled={enCours}
              className={`${CLASSE_BOUTON} border border-gray-200 bg-white text-codialis-gray hover:bg-gray-50`}
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {erreur && (
        <p role="alert" className="text-[11px] text-red-700">
          {erreur}
        </p>
      )}
    </li>
  );
}

/**
 * Immersions d'un candidat, dans la fiche d'entretien : une ligne par
 * entreprise, une réponse oui ou non par ligne, et le bouton « + » pour en
 * ajouter. Chaque geste est enregistré aussitôt — il ne dépend pas du bouton
 * « Enregistrer » de la fiche, et le « oui » prévient le pôle administratif.
 */
export function PositionnementsCandidat({
  apprenantId,
  peutPositionner,
  peutEtablirContrat,
  peutCreerEntreprise = false,
}: {
  apprenantId: string;
  peutPositionner: boolean;
  peutEtablirContrat: boolean;
  /** L'employeur qui manque se crée sur place (`com-entreprises`). */
  peutCreerEntreprise?: boolean;
}): JSX.Element {
  const notifier = useToast();
  const { data, isLoading, error } = usePositionnementsCandidat(apprenantId);
  const positionner = usePositionner();
  const [ajout, setAjout] = useState(false);

  if (isLoading) return <Chargement libelle="Chargement des immersions…" />;
  if (error || !data) {
    return <MessageErreur message="Les immersions du candidat n’ont pas pu être chargées." />;
  }

  return (
    <div className="space-y-4">
      <p className="rounded-xl bg-codialis-sand px-4 py-3 text-[11px] text-codialis-gray">
        Chaque immersion s’enregistre aussitôt, sans attendre le bouton « Enregistrer » de la
        fiche. Le contrat attend le dernier jour de l’immersion ; un positionnement sans
        immersion part tout de suite au pôle administratif.
      </p>

      {data.immersions.length === 0 ? (
        <p className="text-sm text-codialis-gray">Aucune immersion pour ce candidat.</p>
      ) : (
        <ul className="space-y-3">
          {data.immersions.map((immersion) => (
            <LignePositionnement
              key={immersion.id}
              immersion={immersion}
              peutEtablirContrat={peutEtablirContrat}
            />
          ))}
        </ul>
      )}

      {peutPositionner &&
        (ajout ? (
          <div className="space-y-2 rounded-xl border border-dashed border-codialis-sky/40 bg-white p-4">
            <FormulairePositionnement
              entreprises={data.entreprises}
              {...(peutCreerEntreprise ? { creationEntreprise: { site: data.site } } : {})}
              enCours={positionner.isPending}
              onValider={({ entrepriseId, ...saisie }) =>
                positionner.mutate(
                  { ...saisie, apprenantId, entrepriseId: entrepriseId ?? '' },
                  {
                    onSuccess: ({ message }) => {
                      notifier(message);
                      setAjout(false);
                    },
                  },
                )
              }
            >
              <button
                type="button"
                onClick={() => setAjout(false)}
                disabled={positionner.isPending}
                className="min-h-[44px] rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-codialis-gray transition hover:bg-gray-50"
              >
                Annuler
              </button>
            </FormulairePositionnement>
            {messageErreur(positionner.error) && (
              <p role="alert" className="text-[11px] text-red-700">
                {messageErreur(positionner.error)}
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-1">
            <button
              type="button"
              onClick={() => {
                positionner.reset();
                setAjout(true);
              }}
              disabled={data.refusPositionnement !== null}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-codialis-navy/20 px-4 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus className="h-4 w-4" aria-hidden />
              Ajouter une immersion
            </button>
            {data.refusPositionnement && (
              <p className="text-[11px] text-codialis-gray">{data.refusPositionnement}</p>
            )}
          </div>
        ))}
    </div>
  );
}
